import React from "react"
import {
  ThemeProvider,
  createTheme,
  Container,
  Grid,
  Box,
  Typography,
  Avatar,
  Button,
  TextField,
  Link,
  Card,
  CardContent,
} from "@mui/material"
import {
  Email as EmailIcon,
  Phone as PhoneIcon,
  GitHub as GitHubIcon,
  LinkedIn as LinkedInIcon,
  LocationOn as LocationIcon,
  Language as LanguageIcon,
} from "@mui/icons-material"

// Crear un tema personalizado con los colores indigo y teal
const theme = createTheme({
  palette: {
    primary: {
      main: "#4f46e5", // indigo-600
      light: "#818cf8", // indigo-400
      dark: "#3730a3", // indigo-800
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#14b8a6", // teal-500
      light: "#5eead4", // teal-300
      dark: "#0f766e", // teal-700
      contrastText: "#ffffff",
    },
    background: {
      default: "#ffffff",
      paper: "#f8fafc", // slate-50
    },
    text: {
      primary: "#0f172a", // slate-900
      secondary: "#475569", // slate-600
    },
  },
  typography: {
    fontFamily: [
      "-apple-system",
      "BlinkMacSystemFont",
      '"Segoe UI"',
      "Roboto",
      '"Helvetica Neue"',
      "Arial",
      "sans-serif",
    ].join(","),
    h1: {
      fontWeight: 700,
      fontSize: "2.5rem",
    },
    h2: {
      fontWeight: 700,
      fontSize: "2rem",
    },
    h3: {
      fontWeight: 700,
      fontSize: "1.75rem",
    },
    h4: {
      fontWeight: 600,
      fontSize: "1.25rem",
    },
    h5: {
      fontWeight: 600,
      fontSize: "1rem",
    },
    body1: {
      fontSize: "1rem",
    },
    body2: {
      fontSize: "0.875rem",
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: "none",
          fontWeight: 600,
          padding: "8px 16px",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
        },
      },
    },
  },
})

// Datos de experiencia laboral
const workExperienceData = [
  {
    id: 1,
    title: "Full Stack Development (Web & Mobile)",
    description:
      "Developer specialized in React.js, React Native (Expo), Laravel, and Symfony, with experience building complete web and mobile applications. I design modern interfaces, develop RESTful APIs, and work with databases like MySQL, PostgreSQL, and Firebase, always focusing on security, performance, and scalability.",
    image: "/imagen/img02.webp",
  },
  {
    id: 2,
    title: "Automation & Cloud Services",
    description:
      "I implement automated solutions using Python and Selenium (web scraping, bots, data processing) and deploy applications in AWS, using services like EC2, S3, Lambda, SES (email service), and RDS (managed relational databases). I have experience in cloud infrastructure, continuous integration (CI/CD), and secure project management. I also have a registered software product under copyright.",
    image: "/imagen/img04.webp",
  },
]

// Datos de empresas
const companiesData = [
  {
    id: 1,
    name: "Digital Solutions Psychology",
    logo: "/imagen/logo01.png",
    website: "https://d-p-s-s.com/",
    description: "Digital Psychology Solutions",
  },
  {
    id: 2,
    name: "Exequte",
    logo: "/imagen/logo02.png",
    website: "https://www.exequte.com/",
    description: "Operational Effectiveness Platform for Small Companies",
  },
]

// Datos de habilidades
const skillsData = {
  engineering: [
    { id: 1, name: "Laravel" },
    { id: 2, name: "Symfony" },
    { id: 3, name: "React" },
    { id: 4, name: "Next.js" },
    { id: 5, name: "Python" },
    { id: 6, name: "Tailwind CSS" },
  ],
  cloud: [
    { id: 1, name: "AWS EC2" },
    { id: 2, name: "AWS S3" },
    { id: 3, name: "AWS RDS" },
    { id: 4, name: "AWS SES" },
    { id: 5, name: "AWS elastic beanstalk" },
    { id: 6, name: "AWS amplify" },
  ],
  automation: [
    { id: 1, name: "Python" },
    { id: 2, name: "Selenium" },
    { id: 3, name: "Web Scraping" },
  ],
  databases: [
    { id: 1, name: "MySQL" },
    { id: 2, name: "PostgreSQL" },
    { id: 3, name: "Firebase" },
  ],
}

// Componente WorkExperienceCard modificado (sin puntos)
function WorkExperienceCard({ title, description }) {
  return React.createElement(
    Box,
    { sx: { mb: 6, position: "relative" } },
    React.createElement(Box, {
      sx: {
        position: "absolute",
        left: -3,
        top: 0,
        height: "100%",
        width: 2,
        bgcolor: "primary.main",
        display: { xs: "block", md: "none" },
      },
    }),
    React.createElement(Typography, { variant: "h3", color: "primary.main", gutterBottom: true }, title),
    React.createElement(Typography, { variant: "body1", color: "text.secondary", sx: { maxWidth: "md" } }, description),
  )
}

// Componente de insignia de habilidad
function SkillBadge({ name }) {
  return React.createElement(
    Box,
    {
      sx: {
        py: 1,
        px: 1.5,
        border: 1,
        borderColor: "primary.light",
        borderRadius: 1,
        color: "primary.dark",
        fontSize: "0.875rem",
        fontWeight: 500,
      },
    },
    name,
  )
}

// Componente CompanyCard
function CompanyCard({ logo, name, website, description }) {
  return React.createElement(
    Box,
    {
      sx: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        p: 3,
        borderRadius: 2,
        bgcolor: "white",
        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
        transition: "transform 0.2s, box-shadow 0.2s",
        "&:hover": {
          transform: "translateY(-5px)",
          boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
        },
      },
    },
    React.createElement(Avatar, {
      src: logo || "https://placehold.co/100x100",
      alt: name,
      variant: "rounded",
      sx: { width: 80, height: 80, mb: 2 },
    }),
    React.createElement(
      Typography,
      { variant: "h5", color: "primary.main", fontWeight: "bold", gutterBottom: true },
      name,
    ),
    React.createElement(
      Link,
      {
        href: website,
        target: "_blank",
        rel: "noopener noreferrer",
        sx: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.5,
          color: "text.secondary",
          textDecoration: "none",
          mb: 2,
          "&:hover": { color: "primary.main" },
        },
      },
      React.createElement(LanguageIcon, { sx: { fontSize: 16 } }),
      React.createElement(Typography, { variant: "body2" }, website.replace(/(^\w+:|^)\/\//, "")),
    ),
    description && React.createElement(Typography, { variant: "body2", color: "text.secondary" }, description),
  )
}

// Componente principal de la página de perfil
export default function ProfilePage() {
  return React.createElement(
    ThemeProvider,
    { theme: theme },
    React.createElement(
      Box,
      { sx: { minHeight: "100vh", bgcolor: "background.default" } },
      // Hero Section
      React.createElement(
        Box,
        {
          sx: {
            position: "relative",
            height: "50vh",
            minHeight: 400,
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "white",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundImage: "url(/fondo.webp)",
              backgroundSize: "cover",
              backgroundPosition: "center",
              zIndex: 0,
            },
            "&::after": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              background: "linear-gradient(to bottom, rgba(79, 70, 229, 0.8), rgba(15, 23, 42, 0.8))",
              zIndex: 1,
            },
          },
        },
        React.createElement(
          Box,
          {
            sx: {
              position: "relative",
              zIndex: 2,
              textAlign: "center",
              px: 2,
            },
          },
          React.createElement(Avatar, {
            src: "/imagen/photo.jpg",
            alt: "Profile",
            sx: {
              width: 210,
              height: 210,
              border: 4,
              borderColor: "white",
              mx: "auto",
              mb: 3,
            },
          }),
          React.createElement(Typography, { variant: "h1", gutterBottom: true }, "Cesar Bonilla"),
          React.createElement(Typography, { variant: "h4", sx: { mb: 2 } }, "Full Stack & Cloud Developer | React · Laravel · Symfony · AWS"),
          React.createElement(
            Box,
            { sx: { display: "flex", alignItems: "center", justifyContent: "center", gap: 1 } },
            React.createElement(LocationIcon, { fontSize: "small" }),
            React.createElement(Typography, { variant: "body1" }, "Bogotá, Colombia"),
          ),
        ),
      ),

      // About Section
      React.createElement(
        Box,
        {
          sx: {
            py: 8,
            background: "linear-gradient(to right, rgba(240, 253, 250, 0.5), rgba(248, 250, 252, 0.5))",
          },
        },
        React.createElement(
          Container,
          null,
          React.createElement(
            Box,
            { sx: { position: "relative", textAlign: "center", mb: 4 } },
            React.createElement(Typography, { variant: "h2", component: "h2", gutterBottom: true }, "About Me"),
            React.createElement(Box, {
              sx: {
                position: "absolute",
                bottom: -4,
                left: "50%",
                transform: "translateX(-50%)",
                height: 4,
                width: 96,
                borderRadius: 1,
                bgcolor: "primary.main",
              },
            }),
          ),
          React.createElement(
            Box,
            { sx: { maxWidth: "md", mx: "auto", textAlign: "center" } },
            React.createElement(
              Typography,
              { variant: "body1", color: "text.secondary" },
              "I’m a Full Stack Developer with experience building web and mobile applications using React.js, React Native (Expo), Laravel, and Symfony. I specialize in developing modern, secure, and scalable solutions by connecting intuitive interfaces with robust backends and efficient APIs. I work with databases like MySQL, PostgreSQL, and Firebase, and deploy full cloud infrastructures using AWS (EC2, elastic beanstalk, amplify aws, S3, RDS, SES, Lambda). I’ve also developed automation and scraping tools with Python and Selenium. I have a registered software product under copyright and experience leading projects that combine performance, clean architecture, and solid tech. I focus on quality, efficiency, and delivering professional results every time.",
            ),
          ),
        ),
      ),

      // Work Experience Section
      React.createElement(
        Box,
        { sx: { py: 8 } },
        React.createElement(
          Container,
          null,
          React.createElement(
            Grid,
            { container: true, spacing: 4, sx: { maxWidth: "lg", mx: "auto" } },
            React.createElement(
              Grid,
              { item: true, xs: 12, sx: { mb: 4 } },
              React.createElement(
                Typography,
                { variant: "h2", color: "primary.main", gutterBottom: true },
                "Work Experience",
              ),
              React.createElement(Box, { sx: { height: 4, width: 96, borderRadius: 1, bgcolor: "primary.main" } }),
            ),

            // Renderizar experiencia laboral desde el array
            workExperienceData.map((experience, index) => {
              const isEven = index % 2 === 0

              // En pantallas pequeñas, mostrar un diseño vertical con título, imagen pequeña y descripción
              return React.createElement(
                React.Fragment,
                { key: `experience-${experience.id}` },
                // Versión móvil (xs, sm) - Diseño vertical para todas las experiencias
                React.createElement(
                  Grid,
                  {
                    item: true,
                    xs: 12,
                    display: { xs: "block", md: "none" },
                  },
                  React.createElement(
                    Box,
                    { sx: { mb: 6, position: "relative", width: "80vw", mx: "auto" } }, // <- aquí
                    React.createElement(
                      Typography,
                      { variant: "h3", color: "primary.main", gutterBottom: true },
                      experience.title,
                    ),
                    React.createElement(Box, {
                      component: "img",
                      src: experience.image,
                      alt: experience.title,
                      sx: {
                        width: "100%",
                        height: "auto",
                        borderRadius: 2,
                        mb: 2,
                        maxHeight: 200,
                        objectFit: "cover",
                        boxShadow:
                          "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                      },
                    }),
                    React.createElement(
                      Typography,
                      {
                        variant: "body1",
                        color: "text.secondary",
                        sx: { maxWidth: "md" },
                      },
                      experience.description,
                    ),
                  )

                ),

                // Versión desktop (md y superior) - Diseño alternado
                // Texto (izquierda o derecha según paridad)
                React.createElement(
                  Grid,
                  {
                    item: true,
                    md: 6,
                    display: { xs: "none", md: "block" },
                    order: { md: isEven ? 1 : 2 },
                  },
                  React.createElement(WorkExperienceCard, {
                    title: experience.title,
                    description: experience.description,
                  }),
                ),

                // Imagen (derecha o izquierda según paridad)
                React.createElement(
                  Grid,
                  {
                    item: true,
                    md: 6,
                    order: { md: isEven ? 2 : 1 },
                    sx: {
                      display: { xs: "none", md: "flex" }, // Oculto en xs, visible en md+
                      justifyContent: "center",
                      alignItems: "center"
                    },
                  },
                  React.createElement(Box, {
                    component: "img",
                    src: experience.image,
                    alt: experience.title,
                    sx: {
                      maxWidth: "40%",
                      height: "auto",
                      borderRadius: 2,
                      boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
                    },
                  }),
                ),
              )
            }),
          ),
        ),
      ),

      // Skills Section
      React.createElement(
        Box,
        { sx: { py: 8, bgcolor: "background.paper" } },
        React.createElement(
          Container,
          null,
          React.createElement(
            Box,
            { sx: { maxWidth: "lg", mx: "auto" } },
            React.createElement(
              Typography,
              { variant: "h2", color: "primary.main", gutterBottom: true },
              "Skills",
            ),
            React.createElement(Box, { sx: { height: 4, width: 96, borderRadius: 1, bgcolor: "primary.main", mb: 6 } }),

            React.createElement(
              Grid,
              { container: true, spacing: 8 },

              React.createElement(
                Grid,
                { item: true, xs: 12, md: 6 },
                React.createElement(
                  Typography,
                  { variant: "h3", color: "primary.main", gutterBottom: true },
                  "Engineering",
                ),
                React.createElement(
                  Typography,
                  { variant: "body1", color: "text.secondary", sx: { mb: 4 } },
                  "Development of modern web and mobile applications focused on scalability, performance, and maintainability. Implementation of component-based architectures, RESTful APIs, and cloud deployment.",
                ),
                React.createElement(
                  Box,
                  { sx: { display: "flex", gap: 0.5, mb: 2 } },
                  [...Array(5)].map((_, i) =>
                    React.createElement(Box, {
                      key: i,
                      sx: {
                        height: 8,
                        width: 8,
                        borderRadius: "50%",
                        border: 1,
                        borderColor: "primary.main",
                      },
                    }),
                  ),
                ),
                React.createElement(
                  Grid,
                  { container: true, spacing: 1 },
                  skillsData.engineering.map((skill) =>
                    React.createElement(
                      Grid,
                      { item: true, xs: 6, key: skill.id },
                      React.createElement(SkillBadge, { name: skill.name }),
                    ),
                  ),
                ),
              ),

              React.createElement(
                Grid,
                { item: true, xs: 12, md: 6 },
                React.createElement(Typography, { variant: "h3", color: "primary.main", gutterBottom: true }, "Cloud & DevOps"),
                React.createElement(
                  Typography,
                  { variant: "body1", color: "text.secondary", sx: { mb: 4 } },
                  "Deployment of robust cloud infrastructures. Process automation, email delivery services, scalable databases, and system monitoring.",
                ),
                React.createElement(
                  Box,
                  { sx: { display: "flex", gap: 0.5, mb: 2 } },
                  [...Array(5)].map((_, i) =>
                    React.createElement(Box, {
                      key: i,
                      sx: {
                        height: 8,
                        width: 8,
                        borderRadius: "50%",
                        border: 1,
                        borderColor: "primary.main",
                      },
                    }),
                  ),
                ),
                React.createElement(
                  Grid,
                  { container: true, spacing: 1 },
                  skillsData.cloud.map((skill) =>
                    React.createElement(
                      Grid,
                      { item: true, xs: 6, key: skill.id },
                      React.createElement(SkillBadge, { name: skill.name }),
                    ),
                  ),
                ),
              ),


              React.createElement(
                Grid,
                { item: true, xs: 12, md: 6 },
                React.createElement(Typography, { variant: "h3", color: "primary.main", gutterBottom: true }, "Automation & Data"),
                React.createElement(
                  Typography,
                  { variant: "body1", color: "text.secondary", sx: { mb: 4 } },
                  "Task automation with Python. Data extraction via web scraping, intelligent bots, and structured data processing to power web systems.",
                ),
                React.createElement(
                  Box,
                  { sx: { display: "flex", gap: 0.5, mb: 2 } },
                  [...Array(5)].map((_, i) =>
                    React.createElement(Box, {
                      key: i,
                      sx: {
                        height: 8,
                        width: 8,
                        borderRadius: "50%",
                        border: 1,
                        borderColor: "primary.main",
                      },
                    }),
                  ),
                ),
                React.createElement(
                  Grid,
                  { container: true, spacing: 1 },
                  skillsData.automation.map((skill) =>
                    React.createElement(
                      Grid,
                      { item: true, xs: 6, key: skill.id },
                      React.createElement(SkillBadge, { name: skill.name }),
                    ),
                  ),
                ),
              ),

              React.createElement(
                Grid,
                { item: true, xs: 12, md: 6 },
                React.createElement(Typography, { variant: "h3", color: "primary.main", gutterBottom: true }, "Databases"),
                React.createElement(
                  Typography,
                  { variant: "body1", color: "text.secondary", sx: { mb: 4 } },
                  "Design and management of relational and non-relational databases for high-performance applications and scalable systems.",
                ),
                React.createElement(
                  Box,
                  { sx: { display: "flex", gap: 0.5, mb: 2 } },
                  [...Array(5)].map((_, i) =>
                    React.createElement(Box, {
                      key: i,
                      sx: {
                        height: 8,
                        width: 8,
                        borderRadius: "50%",
                        border: 1,
                        borderColor: "primary.main",
                      },
                    }),
                  ),
                ),
                React.createElement(
                  Grid,
                  { container: true, spacing: 1 },
                  skillsData.databases.map((skill) =>
                    React.createElement(
                      Grid,
                      { item: true, xs: 6, key: skill.id },
                      React.createElement(SkillBadge, { name: skill.name }),
                    ),
                  ),
                ),
              ),

            ),
          ),
        ),
      ),

      // Companies Section
      React.createElement(
        Box,
        { sx: { py: 8, bgcolor: "background.paper" } },
        React.createElement(
          Container,
          null,
          React.createElement(
            Box,
            { sx: { maxWidth: "lg", mx: "auto" } },
            React.createElement(Typography, { variant: "h2", color: "primary.main", gutterBottom: true }, "Companies"),
            React.createElement(Box, { sx: { height: 4, width: 96, borderRadius: 1, bgcolor: "primary.main", mb: 6 } }),

            React.createElement(
              Typography,
              { variant: "body1", color: "text.secondary", textAlign: "center", mb: 6 },
              "I have had the privilege of collaborating with these outstanding companies, developing innovative and effective digital solutions.",
            ),

            React.createElement(
              Grid,
              {
                container: true,
                spacing: 4,
                sx: {
                  justifyContent: "center", // Centra horizontalmente
                  alignItems: "center",     // Centra verticalmente (si aplica)
                },
              },

              React.createElement(
                Grid,
                {
                  container: true,
                  spacing: 4,
                  justifyContent: "center",    // ⬅️ CENTRAR horizontalmente
                  alignItems: "stretch",       // ⬅️ MISMO ALTO de columnas
                },
                companiesData.map((company) =>
                  React.createElement(
                    Grid,
                    {
                      item: true,
                      xs: 12,
                      sm: 6,
                      md: 3,
                      key: company.id,
                      sx: { display: "flex" }, // Necesario para alinear el Card dentro
                    },
                    React.createElement(
                      Card,
                      {
                        sx: {
                          width: "100%",             // Asegura que llene el Grid item
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "space-between",
                          borderRadius: 2,
                          boxShadow: 3,
                          p: 2,
                          backgroundColor: "#ffffff",
                        },
                      },
                      // Imagen
                      React.createElement(Box, {
                        sx: {
                          width: "100%",
                          height: 120,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "#ffffff",
                        },
                        children: React.createElement("img", {
                          src: company.logo,
                          alt: company.name,
                          style: {
                            maxHeight: "100%",
                            maxWidth: "100%",
                            objectFit: "contain",
                          },
                        }),
                      }),

                      // Contenido
                      React.createElement(
                        CardContent,
                        { sx: { textAlign: "center", flexGrow: 1 } },
                        /*React.createElement(Typography, {
                          variant: "subtitle1",
                          color: "primary.main",
                          gutterBottom: true,
                          children: company.name,
                        }),*/

                        // Enlace
                        React.createElement(
                          Box,
                          { sx: { textAlign: "center", mb: 2, mt: 2 } },
                          React.createElement(
                            Link,
                            { href: company.website, target: "_blank", rel: "noopener" },
                            company.website,
                          )
                        ),

                        React.createElement(Typography, {
                          variant: "body2",
                          color: "text.secondary",
                          children: company.description,
                        })
                      ),
                    )
                  )
                )
              )

            )

          ),
        ),
      ),

      // Contact Section
      React.createElement(
        Box,
        {
          sx: {
            py: 8,
            background: "linear-gradient(to right, #4338ca, #1e293b)",
            color: "white",
          },
        },
        React.createElement(
          Container,
          null,
          React.createElement(
            Box,
            { sx: { position: "relative", textAlign: "center", mb: 6 } },
            React.createElement(Typography, { variant: "h2", component: "h2", gutterBottom: true }, "Contact"),
            React.createElement(Box, {
              sx: {
                position: "absolute",
                bottom: -4,
                left: "50%",
                transform: "translateX(-50%)",
                height: 4,
                width: 96,
                borderRadius: 1,
                bgcolor: "primary.main",
              },
            }),
          ),
          React.createElement(
            Grid,
            { container: true, spacing: 4, sx: { maxWidth: "md", mx: "auto" } },
            React.createElement(
              Grid,
              { item: true, xs: 12, md: 6 },
              React.createElement(Typography, { variant: "h5", gutterBottom: true }, "Contact Information"),
              React.createElement(
                Box,
                { sx: { display: "flex", flexDirection: "column", gap: 2, mb: 4 } },
                React.createElement(
                  Box,
                  { sx: { display: "flex", alignItems: "center", gap: 1.5 } },
                  React.createElement(EmailIcon, { fontSize: "small" }),
                  React.createElement(Typography, { variant: "body1" }, "cesarbonilla.freelancer@gmail.com"),
                ),
                /*React.createElement(
                  Box,
                  { sx: { display: "flex", alignItems: "center", gap: 1.5 } },
                  React.createElement(PhoneIcon, { fontSize: "small" }),
                  React.createElement(Typography, { variant: "body1" }, "+34 612 345 678"),
                ),
                React.createElement(
                  Box,
                  { sx: { display: "flex", alignItems: "center", gap: 1.5 } },
                  React.createElement(GitHubIcon, { fontSize: "small" }),
                  React.createElement(Typography, { variant: "body1" }, "github.com/juanperez"),
                ),
                React.createElement(
                  Box,
                  { sx: { display: "flex", alignItems: "center", gap: 1.5 } },
                  React.createElement(LinkedInIcon, { fontSize: "small" }),
                  React.createElement(Typography, { variant: "body1" }, "linkedin.com/in/juanperez"),
                ),*/
              ),
              /*React.createElement(
                Button,
                {
                  variant: "contained",
                  sx: {
                    bgcolor: "white",
                    color: "primary.main",
                    "&:hover": {
                      bgcolor: "#f8fafc",
                    },
                  },
                },
                "Descargar CV",
              ),*/
            ),
            React.createElement(
              Grid,
              { item: true, xs: 12, md: 6 },
              React.createElement(Typography, { variant: "h5", gutterBottom: true }, "Send me a message"),
            
              React.createElement(
                Box,
                {
                  sx: {
                    width: { xs: "80%", md: "100%" }, // ⬅️ 80% en pantallas pequeñas
                    mx: { xs: "", md: "auto" }, // ⬅️ Centrado horizontalmente
                  },
                },
                React.createElement(
                  Box,
                  { component: "form", sx: { display: "flex", flexDirection: "column", gap: 2 } },
                  React.createElement(TextField, {
                    placeholder: "Name",
                    fullWidth: true,
                    variant: "outlined",
                    InputProps: {
                      sx: {
                        bgcolor: "rgba(30, 41, 59, 0.5)",
                        color: "white",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "rgba(79, 70, 229, 0.6)",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "rgba(79, 70, 229, 0.8)",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "primary.main",
                        },
                      },
                    },
                    InputLabelProps: {
                      sx: { color: "rgba(255, 255, 255, 0.7)" },
                    },
                  }),
                  React.createElement(TextField, {
                    placeholder: "Email",
                    fullWidth: true,
                    variant: "outlined",
                    InputProps: {
                      sx: {
                        bgcolor: "rgba(30, 41, 59, 0.5)",
                        color: "white",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "rgba(79, 70, 229, 0.6)",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "rgba(79, 70, 229, 0.8)",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "primary.main",
                        },
                      },
                    },
                    InputLabelProps: {
                      sx: { color: "rgba(255, 255, 255, 0.7)" },
                    },
                  }),
                  React.createElement(TextField, {
                    placeholder: "Message",
                    fullWidth: true,
                    multiline: true,
                    rows: 4,
                    variant: "outlined",
                    InputProps: {
                      sx: {
                        bgcolor: "rgba(30, 41, 59, 0.5)",
                        color: "white",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "rgba(79, 70, 229, 0.6)",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "rgba(79, 70, 229, 0.8)",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "primary.main",
                        },
                      },
                    },
                    InputLabelProps: {
                      sx: { color: "rgba(255, 255, 255, 0.7)" },
                    },
                  }),
                  React.createElement(
                    Button,
                    {
                      variant: "contained",
                      color: "primary",
                      sx: { alignSelf: "flex-start" },
                    },
                    "Send"
                  )
                )
              )
            )
            
          ),
        ),
      ),

      // Footer
      React.createElement(
        Box,
        { sx: { py: 3, bgcolor: "#0f172a", color: "white", textAlign: "center" } },
        React.createElement(
          Container,
          null,
          React.createElement(
            Typography,
            { variant: "body2" },
            `© ${new Date().getFullYear()} Cesar Bonilla. All rights reserved.`,
          ),
        ),
      ),
    ),
  )
}
